import type { Coding } from '@medplum/fhirtypes'
import {
  BODY_HEIGHT_CODING,
  BODY_WEIGHT_CODING,
  loincCoding,
  loincCodingOf,
} from '#src/models/loinc.ts'
import {
  calculationMethod,
  type DerivationHistoryInput,
  type DerivationInputDefault,
  type DerivationInputs,
  type DerivationMethod,
  type DerivationRequirement,
  type DerivationRule,
} from '#src/observation-logic/derivations/rules.ts'
import { roundConverted } from '#src/observation-logic/units.ts'
import type { ObservationValue } from '#src/observation-logic/value.ts'
import { nutritionCoding } from '#src/terminology/nutrition.ts'

// The calculations of a dietitian's nutrition assessment: the anthropometrics
// the RD templates work out by hand (ideal body weight, weight change from
// usual), resting energy expenditure, and the estimated needs the Estimated
// Nutritional Needs tool works out from the dietitian's per-kg goals.

const WEIGHT = loincCodingOf(BODY_WEIGHT_CODING)
const HEIGHT = loincCodingOf(BODY_HEIGHT_CODING)
export const IDEAL_BODY_WEIGHT_CODING = loincCoding(
  '50064-5',
  'Ideal body weight'
)
export const USUAL_BODY_WEIGHT_CODING = loincCoding(
  '75292-3',
  'Body weight - Reported --usual'
)
export const RESTING_METABOLIC_RATE_CODING = loincCoding(
  '69429-9',
  'Metabolic rate --resting'
)
const GOAL_WEIGHT = nutritionCoding('goal-body-weight')
const ENERGY_PER_KG_LOW = nutritionCoding('energy-goal-per-kg-low')
const ENERGY_PER_KG_HIGH = nutritionCoding('energy-goal-per-kg-high')
const PROTEIN_PER_KG_LOW = nutritionCoding('protein-goal-per-kg-low')
const PROTEIN_PER_KG_HIGH = nutritionCoding('protein-goal-per-kg-high')

// The participant's earliest charted weight in the six months before the
// visit, from an earlier day: what weight change is measured against when no
// usual weight is reported, over the window malnutrition criteria use.
export const EARLIEST_WEIGHT_6_MONTHS_CODING: Coding = {
  system: 'urn:seen:derivation-history',
  code: 'body-weight-earliest-6-months',
  display: 'Earliest weight in the past 6 months',
}
const EARLIEST_WEIGHT_6_MONTHS: DerivationHistoryInput = {
  coding: EARLIEST_WEIGHT_6_MONTHS_CODING,
  of: WEIGHT,
  withinDays: 183,
}
const CHARTED_NOTE =
  'Measured against the earliest weight charted in the 6 months before the visit, on an earlier day. ASPEN/Academy malnutrition criteria judge loss over 1, 3, 6 and 12 months.'
const USUAL_FALLBACK_NOTE =
  'When no usual weight is reported, measured against the earliest weight charted in the 6 months before the visit.'

// Customary per-kg goals, used until the dietitian records the
// participant's own: the ones dietitians entered most often in Orbit's
// Estimated Nutritional Needs tool.
const ENN_DEFAULT_NOTE =
  'Default from the goals dietitians entered most often in Orbit’s Estimated Nutritional Needs tool (25–30 kcal/kg in about two-thirds of 755 assessments, 1.0–1.2 g protein/kg in about three-quarters). Record the participant’s goal to replace it.'
const PER_KG_DEFAULTS: readonly DerivationInputDefault[] = [
  [ENERGY_PER_KG_LOW, 25, 'kcal/kg/d'],
  [ENERGY_PER_KG_HIGH, 30, 'kcal/kg/d'],
  [PROTEIN_PER_KG_LOW, 1, 'g/kg/d'],
  [PROTEIN_PER_KG_HIGH, 1.2, 'g/kg/d'],
].map(([coding, value, unit]) => ({
  coding: coding as Coding,
  value: { kind: 'number', value: value as number, unit: unit as string },
  note: ENN_DEFAULT_NOTE,
}))

const LB_PER_KG = 1 / 0.45359237
const CM_PER_INCH = 2.54

const BINARY_SEX: DerivationRequirement = {
  binding: 'participant.sex',
  codes: ['male', 'female'],
}
const AGE: DerivationRequirement = { binding: 'participant.age-years' }

const HAMWI_CITATION =
  'Hamwi GJ. Therapy: changing dietary concepts. In: Danowski TS, ed. Diabetes Mellitus: Diagnosis and Treatment. Vol 1. New York: American Diabetes Association; 1964:73–8.'
const HAMWI_NOTE =
  'Uses the participant’s sex as charted. Heights under 5 ft take the 5 ft base weight, as the Estimated Nutritional Needs tool does.'

const METHODS = {
  ibwHamwi: calculationMethod('ibw-hamwi', 'Hamwi', {
    formula:
      'male: 106 lb + 6 lb per inch over 5 ft; female: 100 lb + 5 lb per inch over 5 ft',
    citation: HAMWI_CITATION,
    note: HAMWI_NOTE,
  }),
  percentIbw: calculationMethod('percent-ibw', 'Percent of ideal (Hamwi)', {
    formula: 'weight ÷ ideal body weight (Hamwi) × 100',
    citation: HAMWI_CITATION,
    note: HAMWI_NOTE,
  }),
  adjustedBodyWeight: calculationMethod(
    'adjusted-body-weight',
    'Ideal plus 25% of excess (Hamwi)',
    {
      formula: 'IBW + 0.25 × (weight − IBW), IBW by Hamwi',
      note: `Meant for weight well above ideal (commonly over 125% of ideal); below ideal it falls under the weight. ${HAMWI_NOTE}`,
    }
  ),
  weightChange: calculationMethod('weight-change-from-usual', 'Difference', {
    formula: 'current weight − usual weight',
    note: USUAL_FALLBACK_NOTE,
  }),
  percentWeightChange: calculationMethod(
    'percent-weight-change-from-usual',
    'Percent of usual weight',
    {
      formula: '(current weight − usual weight) ÷ usual weight × 100',
      note: USUAL_FALLBACK_NOTE,
    }
  ),
  weightChangeCharted: calculationMethod(
    'weight-change-charted-6-months',
    'Change over 6 months (charted)',
    {
      formula: 'current weight − earliest charted weight in the past 6 months',
      note: CHARTED_NOTE,
    }
  ),
  percentWeightChangeCharted: calculationMethod(
    'percent-weight-change-charted-6-months',
    'Percent change over 6 months (charted)',
    {
      formula:
        '(current weight − earliest charted weight in the past 6 months) ÷ that weight × 100',
      note: CHARTED_NOTE,
    }
  ),
  reeMifflin: calculationMethod('ree-mifflin-st-jeor', 'Mifflin-St Jeor', {
    formula:
      '10 × weight_kg + 6.25 × height_cm − 5 × age + 5 (male) or − 161 (female)',
    citation:
      'Mifflin MD, St Jeor ST, Hill LA, et al. A new predictive equation for resting energy expenditure in healthy individuals. Am J Clin Nutr 1990;51(2):241–7.',
    url: 'https://doi.org/10.1093/ajcn/51.2.241',
    note: 'Resting expenditure only; activity and stress factors are not applied.',
  }),
  reeHarrisBenedict: calculationMethod(
    'ree-harris-benedict',
    'Harris-Benedict',
    {
      formula:
        'male: 66.5 + 13.75 × weight_kg + 5.003 × height_cm − 6.755 × age; female: 655.1 + 9.563 × weight_kg + 1.850 × height_cm − 4.676 × age',
      citation:
        'Harris JA, Benedict FG. A biometric study of human basal metabolism. Proc Natl Acad Sci USA 1918;4(12):370–3.',
      url: 'https://doi.org/10.1073/pnas.4.12.370',
      note: 'Resting expenditure only; activity and stress factors are not applied.',
    }
  ),
} as const

function quantity(value: number, unit: string): ObservationValue {
  return { kind: 'number', value, unit }
}

function binarySex(inputs: DerivationInputs): 'male' | 'female' | null {
  const value = inputs.variable('participant.sex')
  const code = value?.kind === 'coding' ? value.value[0]?.code : undefined
  return code === 'male' || code === 'female' ? code : null
}

function ageYears(inputs: DerivationInputs): number | null {
  const value = inputs.variable('participant.age-years')
  return value?.kind === 'number' && value.value >= 0 ? value.value : null
}

function positive(value: number | null): value is number {
  return value !== null && value > 0
}

// Hamwi ideal body weight in kg.
export function hamwiIdealBodyWeight(
  heightCm: number,
  sex: 'male' | 'female'
): number {
  const inchesOverFiveFeet = Math.max(0, heightCm / CM_PER_INCH - 60)
  const pounds =
    sex === 'male' ? 106 + 6 * inchesOverFiveFeet : 100 + 5 * inchesOverFiveFeet
  return pounds / LB_PER_KG
}

function idealFrom(inputs: DerivationInputs): number | null {
  const height = inputs.quantity(HEIGHT, 'cm')
  const sex = binarySex(inputs)
  return positive(height) && sex ? hamwiIdealBodyWeight(height, sex) : null
}

export const IDEAL_BODY_WEIGHT_RULE: DerivationRule = {
  id: 'ibw-hamwi',
  precision: 1,
  target: IDEAL_BODY_WEIGHT_CODING,
  inputs: [HEIGHT],
  requires: [BINARY_SEX],
  method: METHODS.ibwHamwi,
  compute: (inputs) => {
    const ideal = idealFrom(inputs)
    return ideal === null ? null : quantity(ideal, 'kg')
  },
}

export const PERCENT_IDEAL_BODY_WEIGHT_RULE: DerivationRule = {
  id: 'percent-ibw-hamwi',
  target: nutritionCoding('percent-ideal-body-weight'),
  inputs: [WEIGHT, HEIGHT],
  requires: [BINARY_SEX],
  method: METHODS.percentIbw,
  compute: (inputs) => {
    const weight = inputs.quantity(WEIGHT, 'kg')
    const ideal = idealFrom(inputs)
    if (!positive(weight) || !positive(ideal)) {
      return null
    }
    return quantity(roundConverted((weight / ideal) * 100, 0), '%')
  },
}

export const ADJUSTED_BODY_WEIGHT_RULE: DerivationRule = {
  id: 'adjusted-body-weight-hamwi',
  precision: 1,
  target: nutritionCoding('adjusted-body-weight'),
  inputs: [WEIGHT, HEIGHT],
  requires: [BINARY_SEX],
  method: METHODS.adjustedBodyWeight,
  compute: (inputs) => {
    const weight = inputs.quantity(WEIGHT, 'kg')
    const ideal = idealFrom(inputs)
    if (!positive(weight) || !positive(ideal)) {
      return null
    }
    return quantity(ideal + 0.25 * (weight - ideal), 'kg')
  },
}

// Current weight and the weight it is measured against: the reported usual
// weight, else the earliest charted one in the past 6 months; or, for the
// charted methods, always that one.
function weightAndBaseline(
  inputs: DerivationInputs,
  baseline: 'usual' | 'charted'
): [number, number] | null {
  const weight = inputs.quantity(WEIGHT, 'kg')
  const charted = inputs.quantity(EARLIEST_WEIGHT_6_MONTHS_CODING, 'kg')
  const against =
    baseline === 'charted'
      ? charted
      : (inputs.quantity(USUAL_BODY_WEIGHT_CODING, 'kg') ?? charted)
  return positive(weight) && positive(against) ? [weight, against] : null
}

function weightChangeRule(
  id: string,
  method: DerivationMethod,
  baseline: 'usual' | 'charted'
): DerivationRule {
  return {
    id,
    precision: 1,
    target: nutritionCoding('weight-change-from-usual'),
    ...(baseline === 'usual'
      ? {
          inputs: [
            WEIGHT,
            USUAL_BODY_WEIGHT_CODING,
            EARLIEST_WEIGHT_6_MONTHS_CODING,
          ],
          optional: [USUAL_BODY_WEIGHT_CODING, EARLIEST_WEIGHT_6_MONTHS_CODING],
        }
      : { inputs: [WEIGHT, EARLIEST_WEIGHT_6_MONTHS_CODING] }),
    history: [EARLIEST_WEIGHT_6_MONTHS],
    method,
    compute: (inputs) => {
      const pair = weightAndBaseline(inputs, baseline)
      return pair ? quantity(pair[0] - pair[1], 'kg') : null
    },
  }
}

function percentWeightChangeRule(
  id: string,
  method: DerivationMethod,
  baseline: 'usual' | 'charted'
): DerivationRule {
  return {
    ...weightChangeRule(id, method, baseline),
    precision: undefined,
    target: nutritionCoding('percent-weight-change-from-usual'),
    compute: (inputs) => {
      const pair = weightAndBaseline(inputs, baseline)
      return pair
        ? quantity(
            roundConverted(((pair[0] - pair[1]) / pair[1]) * 100, 1),
            '%'
          )
        : null
    },
  }
}

export const WEIGHT_CHANGE_RULE = weightChangeRule(
  'weight-change-from-usual',
  METHODS.weightChange,
  'usual'
)
export const WEIGHT_CHANGE_CHARTED_RULE = weightChangeRule(
  'weight-change-charted-6-months',
  METHODS.weightChangeCharted,
  'charted'
)
export const PERCENT_WEIGHT_CHANGE_RULE = percentWeightChangeRule(
  'percent-weight-change-from-usual',
  METHODS.percentWeightChange,
  'usual'
)
export const PERCENT_WEIGHT_CHANGE_CHARTED_RULE = percentWeightChangeRule(
  'percent-weight-change-charted-6-months',
  METHODS.percentWeightChangeCharted,
  'charted'
)

function restingEnergyRule(
  id: string,
  method: DerivationMethod,
  equation: (
    weightKg: number,
    heightCm: number,
    age: number,
    sex: 'male' | 'female'
  ) => number
): DerivationRule {
  return {
    id,
    target: RESTING_METABOLIC_RATE_CODING,
    inputs: [WEIGHT, HEIGHT],
    requires: [BINARY_SEX, AGE],
    method,
    compute: (inputs) => {
      const weight = inputs.quantity(WEIGHT, 'kg')
      const height = inputs.quantity(HEIGHT, 'cm')
      const age = ageYears(inputs)
      const sex = binarySex(inputs)
      if (!positive(weight) || !positive(height) || age === null || !sex) {
        return null
      }
      return quantity(
        roundConverted(equation(weight, height, age, sex), 0),
        'kcal/d'
      )
    },
  }
}

export const REE_MIFFLIN_ST_JEOR_RULE = restingEnergyRule(
  'ree-mifflin-st-jeor',
  METHODS.reeMifflin,
  (weight, height, age, sex) =>
    10 * weight + 6.25 * height - 5 * age + (sex === 'male' ? 5 : -161)
)

export const REE_HARRIS_BENEDICT_RULE = restingEnergyRule(
  'ree-harris-benedict',
  METHODS.reeHarrisBenedict,
  (weight, height, age, sex) =>
    sex === 'male'
      ? 66.5 + 13.75 * weight + 5.003 * height - 6.755 * age
      : 655.1 + 9.563 * weight + 1.85 * height - 4.676 * age
)

// A daily need from a per-kg goal and a weight: the goal weight by default,
// as the Estimated Nutritional Needs tool does, or the current weight.
interface NeedsBasis {
  id: 'goal-weight' | 'current-weight'
  weight: Coding
  display: string
}

const NEEDS_BASES: readonly NeedsBasis[] = [
  { id: 'goal-weight', weight: GOAL_WEIGHT, display: 'goal weight' },
  { id: 'current-weight', weight: WEIGHT, display: 'current weight' },
]

const PER_KG_NOTE =
  'The per-kg goal is the dietitian’s, recorded in the note; until one is recorded, the customary default is used.'
const GOAL_FALLBACK_NOTE =
  'Uses the current weight when no goal weight is recorded.'

function needsRules({
  id,
  target,
  perKg,
  perKgUnit,
  unit,
  label,
  formulaSuffix = '',
}: {
  id: string
  target: Coding
  perKg: Coding
  perKgUnit: string
  unit: string
  label: string
  // What the formula adds after the weight, such as a fluid's 1 mL/kcal.
  formulaSuffix?: string
}): DerivationRule[] {
  return NEEDS_BASES.map((basis) => {
    const fallsBack = basis.weight !== WEIGHT
    return {
      id: `${id}:${basis.id}`,
      target,
      ...(fallsBack
        ? {
            inputs: [perKg, basis.weight, WEIGHT],
            optional: [basis.weight, WEIGHT],
          }
        : { inputs: [perKg, basis.weight] }),
      defaults: PER_KG_DEFAULTS,
      method: calculationMethod(
        `${id}-${basis.id}`,
        `${label} × ${basis.display}`,
        {
          formula: `${perKg.display} × ${basis.display} (kg)${formulaSuffix}`,
          note: fallsBack
            ? `${PER_KG_NOTE} ${GOAL_FALLBACK_NOTE}`
            : PER_KG_NOTE,
        }
      ),
      compute: (inputs: DerivationInputs) => {
        const rate = inputs.quantity(perKg, perKgUnit)
        const weight =
          inputs.quantity(basis.weight, 'kg') ??
          (fallsBack ? inputs.quantity(WEIGHT, 'kg') : null)
        if (!positive(rate) || !positive(weight)) {
          return null
        }
        return quantity(roundConverted(rate * weight, 0), unit)
      },
    }
  })
}

// Fluid at 1 mL per kcal of estimated energy needs, as the RD follow-up
// template writes it.
function fluidRules(
  id: string,
  target: Coding,
  energyPerKg: Coding
): DerivationRule[] {
  return needsRules({
    id,
    target,
    perKg: energyPerKg,
    perKgUnit: 'kcal/kg/d',
    unit: 'mL/d',
    label: '1 mL/kcal',
    formulaSuffix: ' × 1 mL/kcal',
  })
}

export const NUTRITION_DERIVATIONS: readonly DerivationRule[] = [
  IDEAL_BODY_WEIGHT_RULE,
  PERCENT_IDEAL_BODY_WEIGHT_RULE,
  ADJUSTED_BODY_WEIGHT_RULE,
  WEIGHT_CHANGE_RULE,
  WEIGHT_CHANGE_CHARTED_RULE,
  PERCENT_WEIGHT_CHANGE_RULE,
  PERCENT_WEIGHT_CHANGE_CHARTED_RULE,
  REE_MIFFLIN_ST_JEOR_RULE,
  REE_HARRIS_BENEDICT_RULE,
  ...needsRules({
    id: 'estimated-energy-needs-low',
    target: nutritionCoding('estimated-energy-needs-low'),
    perKg: ENERGY_PER_KG_LOW,
    perKgUnit: 'kcal/kg/d',
    unit: 'kcal/d',
    label: 'kcal/kg',
  }),
  ...needsRules({
    id: 'estimated-energy-needs-high',
    target: nutritionCoding('estimated-energy-needs-high'),
    perKg: ENERGY_PER_KG_HIGH,
    perKgUnit: 'kcal/kg/d',
    unit: 'kcal/d',
    label: 'kcal/kg',
  }),
  ...needsRules({
    id: 'estimated-protein-needs-low',
    target: nutritionCoding('estimated-protein-needs-low'),
    perKg: PROTEIN_PER_KG_LOW,
    perKgUnit: 'g/kg/d',
    unit: 'g/d',
    label: 'g/kg',
  }),
  ...needsRules({
    id: 'estimated-protein-needs-high',
    target: nutritionCoding('estimated-protein-needs-high'),
    perKg: PROTEIN_PER_KG_HIGH,
    perKgUnit: 'g/kg/d',
    unit: 'g/d',
    label: 'g/kg',
  }),
  ...fluidRules(
    'estimated-fluid-needs-low',
    nutritionCoding('estimated-fluid-needs-low'),
    ENERGY_PER_KG_LOW
  ),
  ...fluidRules(
    'estimated-fluid-needs-high',
    nutritionCoding('estimated-fluid-needs-high'),
    ENERGY_PER_KG_HIGH
  ),
]
